import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { PageHeader } from '../../components/PageHeader.jsx';
import { FormField } from '../../components/FormField.jsx';

const sourceLabel = {
  settings: 'set here',
  env: 'server default',
  none: 'not set',
};

export default function EmailSettings() {
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ notifyEmail: '', replyTo: '' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const r = await Admin.settings.email.get();
      setCurrent(r);
      setForm({
        notifyEmail: r.notifyEmail || '',
        replyTo: r.replyTo || '',
      });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    setTestResult(null);
    try {
      const payload = {};
      if (form.notifyEmail !== (current?.notifyEmail || '')) payload.notifyEmail = form.notifyEmail;
      if (form.replyTo !== (current?.replyTo || '')) payload.replyTo = form.replyTo;
      if (Object.keys(payload).length === 0) {
        setToast('Nothing to save — all fields unchanged.');
        setTimeout(() => setToast(null), 3000);
        setSaving(false);
        return;
      }
      await Admin.settings.email.save(payload);
      setToast('Email settings saved.');
      setTimeout(() => setToast(null), 3000);
      await refresh();
    } catch (e) {
      setErr(e.data?.issues ? e.data.issues.map((i) => i.message).join(', ') : e.message);
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    setTesting(true);
    setErr(null);
    setTestResult(null);
    try {
      const r = await Admin.settings.email.test();
      setTestResult({
        ok: true,
        message: `Test email sent to ${r.to}. Check that inbox (and spam), and reply to it to test the Reply-To path.`,
      });
    } catch (e) {
      setTestResult({ ok: false, message: e.data?.message || e.message });
    } finally {
      setTesting(false);
    }
  }

  if (loading) return <p className="text-ink-3">Loading…</p>;

  const effectiveNotify = current?.effective?.notifyEmail;
  const effectiveReplyTo = current?.effective?.replyTo;

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Email"
        description="Where the club receives notifications, and where replies to automated email land."
      />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-ink text-canvas px-4 py-2.5 rounded-full text-sm shadow-modal z-50">
          {toast}
        </div>
      )}

      <form onSubmit={save} className="card space-y-5">
        <div>
          <FormField
            label="Notification email"
            hint="New rental inquiries, join requests, and contact messages are emailed here. Use a real inbox someone actually reads."
          >
            <input
              className="input"
              type="email"
              placeholder={current?.envDefaults?.notifyEmail || 'e.g. rentals@example.com'}
              value={form.notifyEmail}
              onChange={(e) => setForm({ ...form, notifyEmail: e.target.value })}
            />
          </FormField>
          <p className="text-xs text-ink-3 mt-1.5">
            In effect: <strong className="text-ink-2">{effectiveNotify || 'none — notifications are not sent'}</strong>
            {' '}· {sourceLabel[current?.source?.notifyEmail] || '—'}
          </p>
        </div>

        <div>
          <FormField
            label="Reply-To address"
            hint="Stamped on automated email (payment links, notifications) so recipients who hit Reply reach a person instead of no-reply."
          >
            <input
              className="input"
              type="email"
              placeholder={current?.envDefaults?.replyTo || 'e.g. rentals@example.com'}
              value={form.replyTo}
              onChange={(e) => setForm({ ...form, replyTo: e.target.value })}
            />
          </FormField>
          <p className="text-xs text-ink-3 mt-1.5">
            In effect: <strong className="text-ink-2">{effectiveReplyTo || 'none — replies to automated mail are lost'}</strong>
            {' '}· {sourceLabel[current?.source?.replyTo] || '—'}
          </p>
        </div>

        {err && (
          <div className="px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>
        )}
        {testResult && (
          <div className={`px-3 py-2 rounded-md text-sm ${testResult.ok ? 'bg-line-soft/40 text-ink-2' : 'bg-danger/10 text-danger'}`}>
            {testResult.message}
          </div>
        )}

        <div className="flex items-center gap-3 pt-2 border-t border-line">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn-outline" onClick={sendTest} disabled={testing}>
            {testing ? 'Sending…' : 'Send test email'}
          </button>
        </div>
      </form>
    </div>
  );
}
